import React, { useState, useEffect } from 'react';

const SponsorsPartnerships = () => {
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnershipModal, setShowPartnershipModal] = useState(false);
  const [partnershipForm, setPartnershipForm] = useState({
    brandName: '',
    contactPerson: '',
    email: '',
    website: '',
    whySponsor: '',
    budgetRange: '',
    collaborationIdeas: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch sponsors from API
  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors/public`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sponsors: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Transform the data to match the expected structure
      const transformedSponsors = data.items?.map(sponsor => ({
        id: sponsor.id,
        name: sponsor.name,
        category: sponsor.category,
        logo: sponsor.logo,
        description: sponsor.description,
        whyPartnered: sponsor.why_partnered || sponsor.whyPartnered,
        website: sponsor.website ? sponsor.website.replace('https://', '').replace('http://', '') : '', // Remove protocol for display
        featured: null, // Could be enhanced later
        testimonial: {
          quote: sponsor.testimonial?.content || '',
          author: sponsor.testimonial?.name && sponsor.testimonial?.role 
            ? `${sponsor.testimonial.name}, ${sponsor.testimonial.role}` 
            : sponsor.testimonial?.name || ''
        }
      })) || [];
      
      setPartners(transformedSponsors);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      // Fallback to empty array if API fails
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const stats = {
    weeklyListeners: '14k',
    avgWatchTime: '71%',
    partnersPerQuarter: '2',
    episodeDownloads: '250k+'
  };

  const handlePartnershipSubmit = async (e) => {
    e.preventDefault();
    if (partnershipForm.brandName && partnershipForm.email) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'partnership',
            name: partnershipForm.brandName,
            email: partnershipForm.email,
            payload: JSON.stringify(partnershipForm)
          })
        });

        if (!response.ok) throw new Error('Submission failed');

        setIsSubmitted(true);
        setTimeout(() => {
          setShowPartnershipModal(false);
          setIsSubmitted(false);
          setPartnershipForm({
            brandName: '',
            contactPerson: '',
            email: '',
            website: '',
            whySponsor: '',
            budgetRange: '',
            collaborationIdeas: ''
          });
        }, 2500);
      } catch (error) {
        console.error('Partnership submission error:', error);
        alert('Failed to submit partnership application. Please try again later.');
      }
    }
  };

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const ExternalLinkIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );

  const MicrophoneIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-midnight text-warm-white font-geist">
      {/* Section Header */}
      <div className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-clash text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
            Izgrađeno sa vizionarskim partnerima
          </h1>
          <p className="text-xl lg:text-2xl text-soft-gray max-w-4xl mx-auto leading-relaxed font-geist">
            Unframed Talks podržavaju brendovi koji vjeruju u iskrene razgovore, 
            mentalno zdravlje osnivača i dugoročno razmišljanje. Ovo nijesu nasumične reklame — ovo 
            su usklađeni partneri koji dijele našu misiju.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="px-6 lg:px-12 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-deep-teal/10 to-electric-yellow/5 backdrop-blur-sm border border-slate-dark/20 rounded-3xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="font-clash text-2xl lg:text-3xl font-bold mb-4 text-electric-yellow">
                Naš domet i uticaj
              </h2>
              <p className="text-soft-gray">
                Saradnju sklapamo sa samo 2 brenda po kvartalu. Evo zašto nas biraju:
              </p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-electric-yellow mb-2">
                  {stats.weeklyListeners}
                </div>
                <div className="text-soft-gray text-sm uppercase tracking-wide">
                  Nedeljnih slušalaca
                </div>
                <div className="text-xs text-soft-gray/70 mt-1">
                  Na Spotify-u i YouTube-u
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-electric-yellow mb-2">
                  {stats.avgWatchTime}
                </div>
                <div className="text-soft-gray text-sm uppercase tracking-wide">
                  Prosječno vrijeme gledanja
                </div>
                <div className="text-xs text-soft-gray/70 mt-1">
                  Visoka stopa angažmana
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-electric-yellow mb-2">
                  {stats.episodeDownloads}
                </div>
                <div className="text-soft-gray text-sm uppercase tracking-wide">
                  Ukupnih preuzimanja
                </div>
                <div className="text-xs text-soft-gray/70 mt-1">
                  I broj brzo raste
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-electric-yellow mb-2">
                  {stats.partnersPerQuarter}
                </div>
                <div className="text-soft-gray text-sm uppercase tracking-wide">
                  Partnera po kvartalu
                </div>
                <div className="text-xs text-soft-gray/70 mt-1">
                  Ekskluzivna partnerstva
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partners Grid */}
      <div className="px-6 lg:px-12 mb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-clash text-3xl lg:text-4xl font-bold mb-12 text-center">
            Naši partneri
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="text-electric-yellow text-lg">Učitava naše nevjerovatne partnere...</div>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-soft-gray text-lg">Trenutno nema aktivnih partnera.</div>
              <p className="text-soft-gray text-sm mt-2">Vratite se uskoro za nova partnerstva!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className="group cursor-pointer"
                  onClick={() => setSelectedPartner(partner)}
                >
                  <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6 hover:scale-105 hover:border-electric-yellow/30 transition-all duration-500">
                    {/* Partner Logo */}
                    <div className="aspect-square mb-4 rounded-xl border border-slate-dark/20 p-4 flex items-center justify-center bg-transparent">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="w-full h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                    
                    {/* Partner Info */}
                    <h3 className="font-bold text-lg mb-1 group-hover:text-electric-yellow transition-colors duration-300">
                      {partner.name}
                    </h3>
                    <p className="text-soft-gray text-sm mb-2">{partner.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Testimonials */}
      {!loading && partners.length > 0 && partners.filter(p => p.testimonial.quote).length > 0 && (
        <div className="px-6 lg:px-12 mb-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-clash text-3xl lg:text-4xl font-bold mb-12 text-center">
              Šta naši partneri kažu
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {partners.filter(partner => partner.testimonial.quote).slice(0, 3).map((partner) => (
                <div
                  key={`testimonial-${partner.id}`}
                  className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6"
                >
                  <blockquote className="text-lg leading-relaxed mb-4 italic text-electric-yellow text-left">
                    "{partner.testimonial.quote}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-slate-dark/20 p-2 flex items-center justify-center bg-transparent">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="w-full h-full object-contain grayscale"
                      />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-sm text-left">{partner.testimonial.author}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-deep-teal/20 to-electric-yellow/10 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-12">
            <h2 className="font-clash text-4xl lg:text-5xl font-bold mb-6">
              Želite da podržite iskrene razgovore?
            </h2>
            <p className="text-xl text-soft-gray mb-8">
              Pridružite se našoj ekskluzivnoj zajednici partnera. Pažljivo biramo brendove koji 
              se usklađuju sa našom misijom autentičnog pripovijedanja osnivača.
            </p>
            
            <button 
              onClick={() => setShowPartnershipModal(true)}
              className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-lg hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              👉 Postanite partner
            </button>
          </div>
        </div>
      </div>

      {/* Partner Detail Modal */}
      {selectedPartner && (
        <div className="fixed inset-0 bg-midnight/95 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-charcoal/90 backdrop-blur-md border border-slate-dark/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setSelectedPartner(null)}
                className="absolute top-6 right-6 text-soft-gray hover:text-electric-yellow transition-colors duration-300"
              >
                <CloseIcon />
              </button>

              {/* Partner Header */}
              <div className="flex items-center gap-6 mb-6">
                <div className="w-20 h-20 rounded-xl border border-slate-dark/20 p-3 flex items-center justify-center bg-transparent flex-shrink-0">
                  <img
                    src={selectedPartner.logo}
                    alt={selectedPartner.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-left">
                  <h2 className="font-clash text-3xl font-bold mb-2 text-left">{selectedPartner.name}</h2>
                  <p className="text-electric-yellow font-medium text-left">{selectedPartner.category}</p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className="text-soft-gray leading-relaxed text-left">{selectedPartner.description}</p>
              </div>

              {/* Why We Partnered */}
              <div className="mb-6">
                <h3 className="font-bold text-xl mb-3 text-electric-yellow text-left">Zašto smo se udružili</h3>
                <p className="text-soft-gray leading-relaxed text-left">{selectedPartner.whyPartnered}</p>
              </div>

              {/* Visit Website Button */}
              <div className="text-left">
                <a
                  href={`https://${selectedPartner.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300 inline-flex items-center gap-2"
                >
                  Posjeti {selectedPartner.name}
                  <ExternalLinkIcon />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partnership Application Modal */}
      {showPartnershipModal && (
        <div className="fixed inset-0 bg-midnight/95 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-charcoal/90 backdrop-blur-md border border-slate-dark/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setShowPartnershipModal(false)}
                className="absolute top-6 right-6 text-soft-gray hover:text-electric-yellow transition-colors duration-300"
              >
                <CloseIcon />
              </button>

              {!isSubmitted ? (
                <>
                  <h3 className="font-clash text-3xl font-bold mb-2 text-electric-yellow">
                    Postanite partner
                  </h3>
                  <p className="text-soft-gray mb-8">
                    Pridružite se našoj ekskluzivnoj zajednici brendova koji podržavaju autentične razgovore sa osnivačima.
                  </p>

                  <form onSubmit={handlePartnershipSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-soft-gray mb-2">
                          Naziv brenda *
                        </label>
                        <input
                          type="text"
                          required
                          value={partnershipForm.brandName}
                          onChange={(e) => setPartnershipForm({...partnershipForm, brandName: e.target.value})}
                          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-soft-gray mb-2">
                          Kontakt osoba
                        </label>
                        <input
                          type="text"
                          value={partnershipForm.contactPerson}
                          onChange={(e) => setPartnershipForm({...partnershipForm, contactPerson: e.target.value})}
                          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-soft-gray mb-2">
                          Email adresa *
                        </label>
                        <input
                          type="email"
                          required
                          value={partnershipForm.email}
                          onChange={(e) => setPartnershipForm({...partnershipForm, email: e.target.value})}
                          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-soft-gray mb-2">
                          Web sajt
                        </label>
                        <input
                          type="url"
                          value={partnershipForm.website}
                          onChange={(e) => setPartnershipForm({...partnershipForm, website: e.target.value})}
                          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Zašto želite da sponzorišete Unframed Talks?
                      </label>
                      <textarea
                        rows={4}
                        value={partnershipForm.whySponsor}
                        onChange={(e) => setPartnershipForm({...partnershipForm, whySponsor: e.target.value})}
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Budžetski opseg
                      </label>
                      <select
                        value={partnershipForm.budgetRange}
                        onChange={(e) => setPartnershipForm({...partnershipForm, budgetRange: e.target.value})}
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                      >
                        <option value="">Odaberite opseg</option>
                        <option value="$1k-$5k">$1k - $5k po kvartalu</option>
                        <option value="$5k-$15k">$5k - $15k po kvartalu</option>
                        <option value="$15k-$30k">$15k - $30k po kvartalu</option>
                        <option value="$30k+">$30k+ po kvartalu</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Ideje za saradnju
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Imate li konkretne ideje o tome kako bismo mogli sarađivati?"
                        value={partnershipForm.collaborationIdeas}
                        onChange={(e) => setPartnershipForm({...partnershipForm, collaborationIdeas: e.target.value})}
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-electric-yellow text-midnight px-6 py-4 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300"
                    >
                      Pošalji prijavu za partnerstvo
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-6">🤝</div>
                  <h3 className="font-clash text-3xl font-bold mb-4 text-electric-yellow">
                    Prijava primljena!
                  </h3>
                  <p className="text-soft-gray text-lg">
                    Hvala vam na interesovanju za partnerstvo sa Unframed Talks. 
                    Pregledat ćemo vašu prijavu i javiti se u roku od 5 radnih dana.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorsPartnerships;